import {CombinedGraphQLErrors, ServerError} from '@apollo/client/errors';
import type {ErrorLink} from '@apollo/client/link/error';
import {clearAuthState, redirectToLogin, stopTokenRefreshInterval} from '../utils/tokenRefresh';

/**
 * Error handler for the Apollo link chain.
 *
 * Lives outside Apollo.ts so it can be tested without constructing the client, which opens a
 * WebSocket at module scope. The 401 branch was silently dead for exactly that reason: nothing
 * could reach it, so nothing noticed when Apollo Client 4 changed the handler's argument from
 * `{graphQLErrors, networkError}` to `{error}` and both names started arriving as `undefined`.
 */
export const handleApolloError: ErrorLink.ErrorHandler = ({error}) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({message, locations, path}) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`, 'color: orange');
    });
    return;
  }

  if (!error) return;

  console.log(`%c [Network error]: ${error}`, 'color: orange');

  // An expired or revoked session: drop the local auth state and send the user to log in.
  // cway-server answers a bad token with 401, but a 403 at the HTTP level is just as final:
  // the server refuses a signed-in admin something with a 200 carrying a FORBIDDEN GraphQL
  // error (handled above), never with a 403 status, so a 403 means the request was turned
  // away before it reached the schema.
  if (ServerError.is(error) && (error.statusCode === 401 || error.statusCode === 403)) {
    console.log(`[Apollo] ${error.statusCode} from the server - redirecting to login page`);
    stopTokenRefreshInterval();
    clearAuthState();
    redirectToLogin();
  }
};
